import { action, computed, observable, observableRef, runInAction } from "mobx";

import type { Command, Message, Models } from "@mainion/protocol";

import type { WorkerStore } from "./WorkerStore.ts";

export type Settings = {
    /** The main controller, like wss://main.example.com. */
    url: string;
    /** The mainions password. */
    password: string;
    /** The LLM workers use. See packages/worker/README.md. */
    llmUrl: string;
    llmKey: string;
    llmModel: string;
};

type Reply = Extract<Message, { type: "reply" }>;

const SETTINGS_KEY = "mainions.settings";
const RETRY_DELAY_MS = 2000;
/** The close code a controller uses for a wrong password. */
const NOT_ALLOWED = 4001;

/** The connection to the main controller. */
export class ConnectionStore {
    @observable accessor settings: Settings = loadSettings();
    @observable accessor status: "offline" | "connecting" | "online" | "refused" = "offline";
    /** The name of the main controller, once it said hello. */
    @observable accessor main: string | undefined = undefined;
    /** The models workers can use, per LLM provider, as the main controller sent them. Empty until it does. */
    @observableRef accessor models: Models = {};

    #workers: WorkerStore;
    #socket: WebSocket | undefined;
    #retry: ReturnType<typeof setTimeout> | undefined;
    #pending = new Map<string, (reply: Reply) => void>();

    constructor(workers: WorkerStore) {
        this.#workers = workers;
    }

    /** Uses a list of models that didn't come from the main controller, like the desktop app's. */
    @action useModels(models: Models): void {
        this.models = models;
    }

    @computed get configured(): boolean {
        return Boolean(this.settings.url && this.settings.password);
    }

    @action save(settings: Settings): void {
        this.settings = settings;

        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
        } catch {
            // Without storage, the settings last until the page closes.
        }

        this.connect();
    }

    /** Forgets the password and disconnects. The address and LLM stay, for logging in again. */
    @action logOut(): void {
        this.save({ ...this.settings, password: "" });
        this.status = "offline";
        this.main = undefined;
    }

    /** Connects, and keeps reconnecting when the connection drops. */
    @action connect(): void {
        clearTimeout(this.#retry);
        this.#socket?.close();
        if (!this.configured) return;

        this.status = "connecting";
        const socket = new WebSocket(this.settings.url);

        this.#socket = socket;

        socket.addEventListener("open", () => {
            this.#send({ type: "subscribe", password: this.settings.password, after: this.#workers.positions });
        });

        socket.addEventListener("message", ({ data }) => {
            const message = JSON.parse(String(data)) as Message;

            runInAction(() => {
                if (message.type === "welcome") {
                    this.status = "online";
                    this.main = message.controller;
                    this.#workers.addController(message.controller);
                }

                if (message.type === "models") this.models = message.models;
                if (message.type === "event") this.#workers.apply(message.event);
            });

            if (message.type === "reply") {
                this.#pending.get(message.id)?.(message);
                this.#pending.delete(message.id);
            }
        });

        socket.addEventListener("close", ({ code }) => {
            if (socket !== this.#socket) return;
            runInAction(() => (this.status = code === NOT_ALLOWED ? "refused" : "offline"));
            for (const [id, resolve] of this.#pending) resolve({ type: "reply", id, error: "The connection dropped." });
            this.#pending.clear();
            if (code !== NOT_ALLOWED) this.#retry = setTimeout(() => this.connect(), RETRY_DELAY_MS);
        });
    }

    /** Sends a command to the main controller and waits for the reply. */
    command(command: Command): Promise<Reply> {
        const id = crypto.randomUUID();

        if (this.status !== "online") return Promise.resolve({ type: "reply", id, error: "Not connected." });

        return new Promise((resolve) => {
            this.#pending.set(id, resolve);
            this.#send({ type: "command", id, command });
        });
    }

    #send(message: Message): void {
        this.#socket?.send(JSON.stringify(message));
    }
}

function loadSettings(): Settings {
    // When a controller serves this app, it's the main controller, so that's the default address.
    // In the desktop app, the page comes from a file, so there's no default.
    const served = typeof location !== "undefined" && location.protocol.startsWith("http");
    const here = served ? `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}` : "";
    const defaults: Settings = { url: here, password: "", llmUrl: "", llmKey: "", llmModel: "" };

    try {
        return { ...defaults, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}") };
    } catch {
        return defaults;
    }
}
